import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { useThemePreferences } from '../theme/ThemeContext';
import { isHighContrast } from '../theme/theme';
import { msUntilNextBucket, timeOfDayBucket, type TimeOfDayBucket } from '../utils/timeOfDay';

// Warm light rising from below, cool light falling from above. These are the
// web's own values — quieter than the Mac sky — so text sitting directly on
// the page stays ≥ 4.5:1 in every bucket. Only the boundaries are shared.
const SKY: Record<TimeOfDayBucket, { warm: string; cool: string }> = {
  dawn: { warm: 'rgba(120,150,255,0.08)', cool: 'rgba(150,170,255,0.06)' },
  morning: { warm: 'rgba(255,176,122,0.10)', cool: 'rgba(125,177,255,0.10)' },
  midday: { warm: 'rgba(255,196,140,0.11)', cool: 'rgba(150,190,255,0.08)' },
  afternoon: { warm: 'rgba(255,150,110,0.11)', cool: 'rgba(150,130,235,0.09)' },
  evening: { warm: 'rgba(120,110,210,0.09)', cool: 'rgba(90,120,210,0.07)' },
};

function skyGradient(bucket: TimeOfDayBucket): string {
  const { warm, cool } = SKY[bucket];
  return (
    `radial-gradient(120vw 80vh at 50% 120%, ${warm}, transparent 56%), ` +
    `radial-gradient(95vw 62vh at 50% -14%, ${cool}, transparent 54%)`
  );
}

function usePrefersReducedTransparency(): boolean {
  const query = '(prefers-reduced-transparency: reduce)';
  const [matches, setMatches] = useState(
    () => typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return matches;
}

/**
 * A faint time-of-day tint on the empty page background, in dark mode only.
 * It sits at z -10 under all content, so the rail, cards and popovers keep
 * their own surfaces and text contrast never depends on the hour.
 */
export function LivingBackground() {
  const { preferences, resolvedMode } = useThemePreferences();
  const reducedTransparency = usePrefersReducedTransparency();
  const active =
    resolvedMode === 'dark' &&
    preferences.livingBackground &&
    !preferences.readingMode &&
    // Off for high contrast however it's turned on (the dyslexia font too).
    !isHighContrast(preferences) &&
    !reducedTransparency;

  // Mounted only while active, so the sky always starts on the current
  // bucket — nothing stale is kept, or painted, while it's off.
  return active ? <TimeOfDaySky /> : null;
}

// Two stacked layers: `front` shows the current bucket. On a change the back
// layer takes the new gradient and the two swap opacity, so the only thing
// that animates is opacity.
type SkyState = { layers: [TimeOfDayBucket, TimeOfDayBucket]; front: 0 | 1 };

function TimeOfDaySky() {
  const [sky, setSky] = useState<SkyState>(() => {
    const bucket = timeOfDayBucket(new Date());
    return { layers: [bucket, bucket], front: 0 };
  });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    // Re-reads the clock and schedules one timeout for the next boundary —
    // half a second past it, so a timer that fires fractionally early still
    // lands in the new bucket.
    const sync = () => {
      clearTimeout(timer);
      const now = new Date();
      const bucket = timeOfDayBucket(now);
      setSky(prev => {
        if (prev.layers[prev.front] === bucket) return prev;
        const back = prev.front === 0 ? 1 : 0;
        const layers: SkyState['layers'] = [...prev.layers];
        layers[back] = bucket;
        return { layers, front: back };
      });
      timer = setTimeout(sync, msUntilNextBucket(now) + 500);
    };
    // Timers stall while the machine sleeps and ignore time-zone changes, so
    // re-check whenever the page comes back into view or focus.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') sync();
    };

    sync();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', sync);
    window.addEventListener('pageshow', sync);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', sync);
      window.removeEventListener('pageshow', sync);
    };
  }, []);

  return (
    <div aria-hidden data-testid="living-background" className="pointer-events-none fixed inset-0 -z-10">
      {sky.layers.map((bucket, i) => (
        <div
          key={i}
          data-testid="sky-layer"
          data-bucket={bucket}
          className={cn(
            'absolute inset-0 transition-opacity ease-linear [transition-duration:2000ms] motion-reduce:transition-none',
            i === sky.front ? 'opacity-100' : 'opacity-0',
          )}
          style={{ backgroundImage: skyGradient(bucket) }}
        />
      ))}
    </div>
  );
}
